import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMessage } from '../context/MessageContext';
import './Navigation.css';

const Navigation = () => {
  const { user, isAdmin, signOut } = useAuth();
  const { unreadCount } = useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const userMenuRef = useRef(null);
  
  // Show Leagues is active on /leagues, /leagues/:id, /super-leagues/:id, but not /leagues/create or /leagues/edit
  const isShowLeaguesActive = location.pathname === '/leagues' || 
    (location.pathname.startsWith('/leagues/') && !location.pathname.startsWith('/leagues/create') && !location.pathname.startsWith('/leagues/edit')) ||
    location.pathname.startsWith('/super-leagues/');
  const isCreateLeagueActive = location.pathname === '/leagues/create';
  const isMessagesActive = location.pathname === '/messages';

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleMyProfileClick = () => {
    setShowUserMenu(false);
    navigate(`/players/${user.userId}`);
  };

  const handleProfileClick = () => {
    setShowUserMenu(false);
    navigate('/settings');
  };

  const handleLogoutClick = () => {
    setShowUserMenu(false);
    handleLogout();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  useEffect(() => {
    setMenuOpen(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window === 'undefined') {
        return;
      }
      setIsMobileView(window.innerWidth <= 768);
    };

    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);

    return () => {
      window.removeEventListener('resize', updateIsMobile);
    };
  }, []);

  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to={user ? '/leagues' : '/'} className="nav-brand">
          <img src="/logo.png" alt="Playthlon" className="nav-logo" />
          <span className="nav-brand-text">Playthlon</span>
        </Link>
        <button
          className={`nav-toggle ${menuOpen ? 'open' : ''}`}
          onClick={toggleMenu}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation"
          type="button"
        >
          <span className="nav-toggle-bar" />
          <span className="nav-toggle-bar" />
          <span className="nav-toggle-bar" />
        </button>

        <div className={`nav-items ${menuOpen ? 'open' : ''}`}>
          <div className="nav-links">
            {/* Questionnaire link removed - level is now per-sport */}
            <Link to="/leagues" className={`nav-link ${isShowLeaguesActive ? 'active' : ''}`}>
              Show Leagues
            </Link>
            <Link to="/leagues/create" className={`nav-link ${isCreateLeagueActive ? 'active' : ''}`}>
              Create League
            </Link>
            {user && (
              <Link to="/messages" className={`nav-link ${isMessagesActive ? 'active' : ''} nav-link-messages`}>
                Messages
                {unreadCount > 0 && (
                  <span className="nav-message-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin" className={`nav-link ${location.pathname === '/admin' ? 'active' : ''}`}>
                Admin
              </Link>
            )}
          </div>

          {user && (
            isMobileView ? (
              <div className="nav-mobile-user">
                <div className="nav-mobile-user-label">Signed in as {user.name}</div>
                <button className="nav-mobile-action" onClick={handleMyProfileClick}>
                  Profile
                </button>
                <button className="nav-mobile-action" onClick={handleProfileClick}>
                  Settings
                </button>
                <button className="nav-mobile-action destructive" onClick={handleLogoutClick}>
                  Logout
                </button>
              </div>
            ) : (
              <div className="nav-user" ref={userMenuRef}>
                <button className="user-name" onClick={toggleUserMenu}>
                  {user.name}
                  <span className="dropdown-arrow">{showUserMenu ? '▲' : '▼'}</span>
                </button>
                {showUserMenu && (
                  <div className="user-dropdown">
                    <button className="dropdown-item" onClick={handleMyProfileClick}>
                      Profile
                    </button>
                    <button className="dropdown-item" onClick={handleProfileClick}>
                      Settings
                    </button>
                    <button className="dropdown-item" onClick={handleLogoutClick}>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;

